import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Send,
  CheckCircle,
  Calendar,
  MessageCircle,
  FileText,
  Users,
  Sparkles,
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import StatsCard from "./StatsCard";
import MessageChart from "./MessageChart";
import RecentActivity from "./RecentActivity";
import QuickActions from "./QuickActions";
import Layout from "../Layout/Layout";
import Loader from "../utils/Loader";
import { API_BASE_URL } from "../../config";
import axios from "axios";

const Dashboard = () => {
  const { theme } = useTheme();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groups, setGroups] = useState([]);
  const [userName, setUserName] = useState(null);

  useEffect(() => {
    const fetchGroupDetails = async () => {
      try {
        const token = window.__appSession.getToken();
        if (!token) return;

        const response = await fetch(
          `${API_BASE_URL}/api/whatsapp/group-details`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({}),
          },
        );

        const data = await response.json();
        if (response.ok && data.groupDetails?.result) {
          setGroups(data.groupDetails.result);
        }
      } catch (err) {
        console.error("Error fetching group details:", err);
      }
    };

    fetchGroupDetails();
  }, []);

  useEffect(() => {
    const fetchUsername = async () => {
      const token = window.__appSession.getToken();
      try {
        const messagesResponse = await axios.get(
          `${API_BASE_URL}/api/auth/profile`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (messagesResponse.data.result) {
          const userData = messagesResponse.data.result;
          setUserName(userData.name);
          window.__appSession.setItem("userName", userData.name);
        }
      } catch (error) {
        console.error("Error fetching username:", error);
      }
    };

    fetchUsername();
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = window.__appSession.getToken();
        const response = await fetch(
          `${API_BASE_URL}/api/dashboard`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) throw new Error("Failed to fetch dashboard data");
        const data = await response.json();
        setDashboardData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const stats = dashboardData
    ? [
        {
          title: "Messages Sent",
          value: dashboardData.stats.messagesSent,
          change: 12,
          icon: Send,
          description: "Total delivered",
        },
        {
          title: "Success Rate",
          value: dashboardData.messages?.length > 0 ? "100%" : "0%",
          change: 5,
          icon: CheckCircle,
          description: "Delivery rate",
        },
        {
          title: "Scheduled",
          value: dashboardData.stats.scheduledMessages,
          change: 3,
          icon: Calendar,
          description: "Upcoming",
        },
        {
          title: "Birthday",
          value: dashboardData.stats.birthdayMessages,
          change: 8,
          icon: MessageCircle,
          description: "Auto wishes",
        },
        {
          title: "Templates",
          value: dashboardData.stats.templates,
          change: 2,
          icon: FileText,
          description: "Available",
        },
        {
          title: "Groups",
          value: groups.length,
          change: 1,
          icon: Users,
          description: "Active",
        },
      ]
    : [];

  if (loading)
    return (
      <Layout>
        <div className="min-h-[80vh] flex items-center justify-center">
          <Loader theme={theme} text="Loading dashboard..." />
        </div>
      </Layout>
    );

  if (error)
    return (
      <Layout>
        <div className="max-w-7xl mx-auto">
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
            <p className="text-red-600">Error: {error}</p>
          </div>
        </div>
      </Layout>
    );

  const welcomeName = window.__appSession.getItem("userName") || userName;

  return (
    <Layout>
      <div className="mx-auto max-w-7xl space-y-3">
        <motion.section
          className="glass-surface-strong rounded-2xl border border-indigo-200/70 px-3 py-3 sm:px-4"
          initial={{ y: 14, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-indigo-700">
                <Sparkles className="h-3 w-3" />
                Insights
              </span>
              <h1 className="mt-1 bg-gradient-to-r from-indigo-700 via-violet-700 to-indigo-700 bg-clip-text text-xl font-bold text-transparent sm:text-2xl">
                Dashboard Overview
              </h1>
              <p className="mt-1 text-xs text-slate-600 sm:text-sm">
                Welcome back,
                <span className="ml-1 font-semibold capitalize text-indigo-700">
                  {welcomeName}
                </span>
                . Your automation pulse for today.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:w-[320px]">
              <div className="rounded-xl border border-indigo-200 bg-white/80 px-2.5 py-2 text-center">
                <p className="text-[11px] text-slate-500">Templates</p>
                <p className="text-base font-bold text-slate-900">
                  {dashboardData?.stats?.templates ?? 0}
                </p>
              </div>
              <div className="rounded-xl border border-indigo-200 bg-white/80 px-2.5 py-2 text-center">
                <p className="text-[11px] text-slate-500">Groups</p>
                <p className="text-base font-bold text-slate-900">{groups.length}</p>
              </div>
            </div>
          </div>
        </motion.section>

        <section>
          <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-6">
            {stats.map((stat, index) => (
              <StatsCard key={stat.title} {...stat} index={index} />
            ))}
          </div>
        </section>

        <section>
          <div className="grid grid-cols-1 gap-2 lg:grid-cols-2">
            <MessageChart chartData={dashboardData.chartData} />
            <RecentActivity activities={dashboardData.recentActivities} />
          </div>
        </section>

        <section>
          <QuickActions />
        </section>
      </div>
    </Layout>
  );
};

export default Dashboard;
